import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { IUserGroup } from '@core/interfaces/user-group.interface';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { UserGroupService } from '@core/services/user-group.service';
import { UserInvitationService } from '@core/services/user-invitation.service';
import { EEventType } from '@enums/event-tracking.enum';
import { ERoles } from '@enums/roles.enum';
import { IUserInvitation } from '@interfaces/user-invitation.interface';
import { ComponentStore } from '@ngrx/component-store';
import { toEventInvitationSource } from '@utilities/event-payload-mapping.util';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

// Roles/teams/users are no longer owned by this store - they live in the root-provided
// UserManagementSharedDataStore, shared with the Roles & Teams tab and every dialog (see that
// service's doc comment). This store keeps only what's specific to the Users tab: invitations
// (the table's actual row source, covering joined/pending/expired members alike) and the current
// group's seat usage.
// Each of the 3 independent effects below (fetchCurrentUserGroup, fetchInvitations,
// sendInvitation) gets its own loading/error pair rather than sharing one - they can, and in
// this component's constructor do, run concurrently, so a single shared `loading`/`error` field
// would have whichever effect settles last silently clobber the still-relevant state left behind
// by the other (e.g. an error from one effect getting wiped by the other's unrelated success).
interface IUserManagementUsersTabState {
  userInvitations: IUserInvitation[] | null;
  invitationsLoading: boolean;
  invitationsError: string | null;
  userInvitation: IUserInvitation | null;
  sendInvitationLoading: boolean;
  sendInvitationError: string | null;
  userGroup: IUserGroup | null;
  userGroupLoading: boolean;
  userGroupError: string | null;
}

const initialState: IUserManagementUsersTabState = {
  invitationsError: null,
  invitationsLoading: false,
  sendInvitationError: null,
  sendInvitationLoading: false,
  userGroup: null,
  userGroupError: null,
  userGroupLoading: false,
  userInvitation: null,
  userInvitations: null,
};

@Injectable()
export class UserManagementUsersTabStore extends ComponentStore<IUserManagementUsersTabState> {
  public readonly userInvitations$ = this.selectSignal((state: IUserManagementUsersTabState) => state.userInvitations);
  public readonly invitationsLoading$ = this.selectSignal(
    (state: IUserManagementUsersTabState) => state.invitationsLoading,
  );
  public readonly invitationsError$ = this.selectSignal(
    (state: IUserManagementUsersTabState) => state.invitationsError,
  );
  public readonly userInvitation$ = this.selectSignal((state: IUserManagementUsersTabState) => state.userInvitation);
  public readonly sendInvitationLoading$ = this.selectSignal(
    (state: IUserManagementUsersTabState) => state.sendInvitationLoading,
  );
  public readonly sendInvitationError$ = this.selectSignal(
    (state: IUserManagementUsersTabState) => state.sendInvitationError,
  );
  public readonly userGroup$ = this.selectSignal((state: IUserManagementUsersTabState) => state.userGroup);
  public readonly userGroupLoading$ = this.selectSignal(
    (state: IUserManagementUsersTabState) => state.userGroupLoading,
  );
  public readonly userGroupError$ = this.selectSignal((state: IUserManagementUsersTabState) => state.userGroupError);

  /** Set the instant `fetchCurrentUserGroup` is triggered, cleared once it settles either way -
   * see ensureUserGroupLoaded's doc comment for why this exists. */
  private userGroupInFlight = false;

  public readonly fetchCurrentUserGroup = this.effect((userUpdate$: Observable<void>) => {
    return userUpdate$.pipe(
      tap(() => this.patchState({ userGroupError: null, userGroupLoading: true })),
      switchMap(() =>
        this.userGroupService.getCurrentUserGroup(new HttpParams().set('joins', 'all')).pipe(
          tap((userGroup: IUserGroup) => {
            this.userGroupInFlight = false;
            this.patchState({ userGroup, userGroupLoading: false });
          }),
          catchError(() => {
            this.userGroupInFlight = false;
            this.patchState({
              userGroupError: 'Failed to fetch current user group',
              userGroupLoading: false,
            });

            return EMPTY;
          }),
        ),
      ),
    );
  });

  /** Guarded retry for `fetchCurrentUserGroup` - without this, a failed initial load left
   * `userGroup` permanently `null` (the "Seats Used" KPI's `seatsLoading` computed, in
   * UserManagementUsersTabComponent, never becomes false) with no way to recover short of a full
   * route revisit, since nothing else in this component ever re-triggers the fetch. Mirrors
   * ensureInvitationsLoaded exactly - see that method's doc comment for the fuller reasoning. */
  public ensureUserGroupLoaded(): void {
    if (this.userGroup$() !== null || this.userGroupInFlight) {
      return;
    }

    this.userGroupInFlight = true;
    this.fetchCurrentUserGroup();
  }

  /** Set the instant `fetchInvitations` is triggered, cleared once it settles either way - see
   * ensureInvitationsLoaded's doc comment for why this exists. */
  private invitationsInFlight = false;

  public readonly fetchInvitations = this.effect((userUpdate$: Observable<void>) => {
    return userUpdate$.pipe(
      tap(() => this.patchState({ invitationsError: null, invitationsLoading: true })),
      switchMap(() =>
        this.userInvitationService
          .getCampaignSubUserInvitations(new HttpParams().set('limit', '1000').set('order_by', '-expire'))
          .pipe(
            tap((userInvitations: IApiResponse<IUserInvitation[]>) => {
              this.invitationsInFlight = false;
              this.patchState({
                invitationsLoading: false,
                userInvitations: userInvitations.data ?? [],
              });
            }),
            // Deliberately leaves `userInvitations` untouched (still `null` on a genuine initial
            // load failure) rather than writing `[]` - see UserManagementSharedDataStore's class
            // doc comment for why an authoritative empty list would be worse than staying null.
            catchError(() => {
              this.invitationsInFlight = false;
              this.patchState({
                invitationsError: 'Failed to get campaign sub user invitations',
                invitationsLoading: false,
              });

              return EMPTY;
            }),
          ),
      ),
    );
  });

  /** Guarded retry for `fetchInvitations` - without this, a failed initial load left
   * `userInvitations` permanently `null` (the datatable's `isInitialDataLoaded` never becomes
   * true) with no way to recover short of a full route revisit, since nothing else in this
   * component ever re-triggers the fetch. Called from the component constructor instead of
   * `fetchInvitations()` directly, and again from `onDataRefresh` (every datatable
   * sort/filter/page interaction) so any such interaction after a failed load doubles as a retry,
   * without ever double-fetching once the load has actually succeeded. */
  public ensureInvitationsLoaded(): void {
    if (this.userInvitations$() !== null || this.invitationsInFlight) {
      return;
    }

    this.invitationsInFlight = true;
    this.fetchInvitations();
  }

  public readonly sendInvitation = this.effect(
    (userUpdate$: Observable<{ email: string; message: string; roleId?: string; teamId?: string | null }>) => {
      return userUpdate$.pipe(
        tap(() => this.patchState({ sendInvitationError: null, sendInvitationLoading: true, userInvitation: null })),
        switchMap(({ email, message, roleId, teamId }) =>
          this.userInvitationService.sendCampaignSubUserInvitation(email, message, roleId, teamId).pipe(
            tap((userInvitation: IUserInvitation) => {
              this.patchState({ sendInvitationLoading: false, userInvitation });

              this.eventTrackingService.track(EEventType.teamMemberAdded);
              this.eventTrackingService.track(EEventType.userInvitationSent, {
                source: toEventInvitationSource(ERoles.campaignSub),
              });
            }),
            catchError(() => {
              this.patchState({
                sendInvitationError: 'Failed to send campaign sub user invitation',
                sendInvitationLoading: false,
                userInvitation: null,
              });

              return EMPTY;
            }),
          ),
        ),
      );
    },
  );

  constructor(
    private readonly userInvitationService: UserInvitationService,
    private readonly userGroupService: UserGroupService,
    private readonly eventTrackingService: EventTrackingService,
  ) {
    super(initialState);
  }
}
